import Image from "next/image";
import StickyBox from "react-sticky-box";
import classNames from "classnames";
import VideoCard from "./videos/video-card";
import TrendingNow from "./core/trending-now";
import SideBar from "./sidebar/sidebar";
import Layout from "./layout/layout";
import { InfiniteList, NewsList } from "./core/infinite-list";
import { useEffect, useMemo, useRef, useState } from "react";
import { Tab } from "@headlessui/react";
import { Trans } from "@lingui/macro";
import { useRouter } from "next/router";
import { PagePublisherComp } from "@graphql/generated/page";
import {
  usePublisherNewsQuery,
  usePublisherShortsQuery,
  usePublisherVideosQuery,
} from "@graphql/generated/graphql";
import {
  PublisherNewsList,
  PublisherShortsGrid,
  PublisherVideosList,
} from "./publishers/publishers-contents-list";

const PublisherPage: PagePublisherComp = ({
  data: {
    publisher: {
      slug,
      name,
      bigImageUrl,
      description,
      badges,
      hasNews,
      hasVideos,
      hasShorts,
    },
  },
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const router = useRouter();
  const { query } = router;

  const {
    data: newsData,
    fetchMore: newsFetchMore,
    error: newsError,
    loading: newsLoading,
  } = usePublisherNewsQuery({
    variables: {
      id: slug,
      limit: 20,
    },
  });

  const {
    data: videosData,
    fetchMore: videosFetchMore,
    error: videosError,
    loading: videosLoading,
  } = usePublisherVideosQuery({
    variables: {
      id: slug,
      limit: 20,
    },
  });

  const {
    data: shortsData,
    fetchMore: shortsFetchMore,
    error: shortsError,
    loading: shortsLoading,
  } = usePublisherShortsQuery({
    variables: {
      id: slug,
      limit: 20,
    },
  });

  const isLoading = newsLoading || videosLoading || shortsLoading;
  const isError = newsError || videosError || shortsError;

  const hasMoreNews = useMemo(() => {
    return newsData != undefined && newsData.publisher.news.length < 100;
  }, [newsData]);
  const hasMoreVideos = useMemo(() => {
    return videosData != undefined && videosData.publisher.videos.length < 100;
  }, [videosData]);
  const hasMoreShorts = useMemo(() => {
    return shortsData != undefined && shortsData.publisherShorts.length < 100;
  }, [shortsData]);

  useEffect(() => {
    const view = (query.view as string) ?? "news".toLowerCase();
    if (hasVideos && view === "videos") {
      setSelectedIndex(1);
    } else if (hasShorts && view === "shorts") {
      setSelectedIndex(2);
    } else {
      setSelectedIndex(0);
    }
  }, [query, hasVideos, hasShorts]);

  return (
    <Layout
      showNewsStacks={true}
      showCTA={false}
      showFooter={false}
      className="!overflow-visible"
      retainWholePage={true}
    >
      <div className="mx-auto mt-8 max-w-7xl items-start space-x-16 px-4 pb-8 lg:flex">
        <section className="flex w-full flex-1 flex-col">
          <header className="pt-4">
            {/* Publisher Icon and Name */}
            <div className="flex flex-col pb-4">
              <div className="flex items-center space-x-4 md:items-start">
                <div className="flex flex-col">
                  <div
                    className="relative h-[48px] w-[76px] md:h-[101px] md:w-[161px]"
                    style={{
                      filter:
                        "drop-shadow(1px 1px 8px rgba(147, 154, 188, 0.4)",
                    }}
                  >
                    <Image
                      key={slug}
                      className="rounded-lg"
                      src={bigImageUrl}
                      alt={name}
                      layout="fill"
                      objectFit="cover"
                      priority
                    />
                  </div>
                </div>
                <div className="flex flex-col">
                  <span className="font-poppins text-sm font-bold text-text-darkest">
                    {name}
                  </span>
                  <div className="hidden md:block">
                    <PublisherInfo description={description} badges={badges} />
                  </div>
                </div>
              </div>
              <div className="block md:hidden">
                <PublisherInfo description={description} badges={badges} />
              </div>
            </div>

            {/* TODO: need to add publisher categories in the future */}
          </header>

          {isLoading || isError ? (
            <div className="flex flex-1 flex-col space-y-8">
              <p className="py-4 text-center font-sen text-text-lightest">
                <Trans>Loading...</Trans>
              </p>
            </div>
          ) : (
            <>
              <Tab.Group
                selectedIndex={selectedIndex}
                onChange={setSelectedIndex}
              >
                <Tab.List className="flex border-b-2 border-light-background md:block ">
                  {hasNews && newsData.publisher.news.length > 0 && (
                    <Tab
                      className={({ selected }) =>
                        classNames(
                          "mt-6 flex-1 pb-1 font-sen md:mr-10 lg:mt-4",
                          selected
                            ? "border-b-2 border-primary font-bold text-primary"
                            : "text-darkest"
                        )
                      }
                    >
                      <span>
                        <Trans>Articles</Trans>
                      </span>
                    </Tab>
                  )}
                  {hasVideos && videosData.publisher.videos.length > 0 && (
                    <Tab
                      className={({ selected }) =>
                        classNames(
                          "mt-6 flex-1 pb-1 font-sen md:mr-10 lg:mt-4",
                          selected
                            ? "border-b-2 border-primary font-bold text-primary"
                            : "text-darkest"
                        )
                      }
                    >
                      <span>
                        <Trans>Videos</Trans>
                      </span>
                    </Tab>
                  )}
                  {hasShorts && shortsData.publisherShorts.length > 0 && (
                    <Tab
                      className={({ selected }) =>
                        classNames(
                          "mt-6 flex-1 pb-1 font-sen md:mr-10 lg:mt-4",
                          selected
                            ? "border-b-2 border-primary font-bold text-primary"
                            : "text-darkest"
                        )
                      }
                    >
                      <span>
                        <Trans>Shorts</Trans>
                      </span>
                    </Tab>
                  )}
                </Tab.List>
                <Tab.Panels>
                  {hasNews && newsData.publisher.news.length > 0 && (
                    <Tab.Panel>
                      <PublisherNewsList
                        infiniteListProps={{
                          infiniteLoadingOption: {
                            hasMore: hasMoreNews,
                            dataLength: newsData.publisher.news.length || 0,
                            next: () => {
                              newsFetchMore({
                                variables: {
                                  offset: newsData.publisher.news.length,
                                },
                              });
                            },
                            loader: (
                              <p className="py-4 text-center font-sen text-text-lightest">
                                <Trans>Loading...</Trans>
                              </p>
                            ),
                          },
                        }}
                        news={newsData.publisher.news}
                      />
                    </Tab.Panel>
                  )}
                  {hasVideos && videosData.publisher.videos.length > 0 && (
                    <Tab.Panel>
                      <PublisherVideosList
                        infiniteListProps={{
                          infiniteLoadingOption: {
                            hasMore: hasMoreVideos,
                            dataLength: videosData.publisher.videos.length || 0,
                            next: () => {
                              videosFetchMore({
                                variables: {
                                  offset: videosData.publisher.videos.length,
                                  id: slug,
                                },
                              });
                            },
                            loader: (
                              <p className="py-4 text-center font-sen text-text-lightest">
                                <Trans>Loading...</Trans>
                              </p>
                            ),
                          },
                        }}
                        videos={videosData.publisher.videos}
                      />
                    </Tab.Panel>
                  )}
                  {hasShorts && shortsData.publisherShorts.length > 0 && (
                    <Tab.Panel>
                      <PublisherShortsGrid
                        infiniteListProps={{
                          infiniteLoadingOption: {
                            hasMore: hasMoreShorts,
                            dataLength: shortsData.publisherShorts.length || 0,
                            next: () => {
                              shortsFetchMore({
                                variables: {
                                  offset: shortsData.publisherShorts.length,
                                  id: slug,
                                },
                              });
                            },
                            loader: (
                              <p className="py-4 text-center font-sen text-text-lightest">
                                <Trans>Loading...</Trans>
                              </p>
                            ),
                          },
                        }}
                        shorts={shortsData.publisherShorts}
                      />
                    </Tab.Panel>
                  )}
                </Tab.Panels>
              </Tab.Group>
              {newsData.publisher.news.length >= 100 && (
                <div className="py-8">
                  <TrendingNow />
                </div>
              )}
            </>
          )}
        </section>
        <StickyBox offsetTop={20} offsetBottom={20}>
          <SideBar />
        </StickyBox>
      </div>
    </Layout>
  );
};

interface PublisherInfoProps {
  badges: String[];
  description: String;
}

const PublisherInfo: React.FunctionComponent<PublisherInfoProps> = ({
  badges,
  description,
}) => {
  return (
    <div>
      <p className="mt-2 font-sen text-sm leading-5 text-text-dark">
        {description}
      </p>
      {badges?.length > 0 && (
        <div className="no-scrollbar -mx-2 mt-2 flex flex-nowrap overflow-x-scroll pl-2 md:flex-wrap">
          {badges.map((badge) => (
            <span
              key={badges.indexOf(badge)}
              className="mr-2 whitespace-nowrap rounded-lg bg-light-background px-2 py-2 font-sen text-sm font-bold text-black md:mb-2"
            >
              {badge}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default PublisherPage;
